import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Image, Row, Stack, Alert } from 'react-bootstrap';
import CustomCheckbox from '../../components/common/CustomCheckbox';
import { FaInfoCircle } from "react-icons/fa";
import LOGIN_BANNER from "../../assets/images/login-banner.svg";
import LOGO_WHITE from '../../assets/images/logo-white.svg';
import { regenerate2faAPI, verify2faAPI, verifyToResetTOTPAPI } from '../../services/auth.service';
import { ForgotSchema } from './validation';
import storage from '../../helpers/storage';
import sessionStorage from '../../helpers/sessionStorage';
import { getDeviceID } from '../../helpers/utils';
import { toast } from 'react-toastify';
import CustomInputText from '../../components/common/CustomInputText';
import CommonFormikComponent from '../../components/common/CommonFormikComponent';
import Loader from '../../components/common/loader';
import { useCustomStore } from '../../redux/store';
import { redirectionRoutes } from '../../routes/redirectionRoutes';

type FormValues = {
    email: string;
    code: string;
    rememberDevice: boolean;
    deviceID: string;
}

export default function TwoFactorAuthentication() {
    const AppName = process.env.REACT_APP_NAME;
    const location = useLocation();
    const { email, auth0Login, rememberDevice, lostDevice, isChildUser } = location.state;
    
    const { setUser, setAuth0Login } = useCustomStore();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const [timer, setTimer] = useState(60); // Timer countdown in seconds
    const coolDownTime = 60; // CoolDown time in seconds

    const [isMounted, setIsMounted] = useState(false)
    const isVerifyingTOResetTOTP = useRef(false);
    
    /**
     * Initial Form Values
     */
    const initialValues: FormValues = {
        email: email || "",
        code: "",
        rememberDevice: rememberDevice || false,
        deviceID: email ? getDeviceID(email) : ''
    };

    useEffect(() => {
        // A small timeout is sometimes helpful if there are layout shifts
        const timer = setTimeout(() => {
            const codeInputRef = document.getElementById("code");
            if (codeInputRef) {
                codeInputRef.focus();
            }
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    /**
     * component mounted hook
     */
    useEffect(() => {
        // This code will run when the component is mounted
        setIsMounted(true);
        // Define cleanup logic here
        return () => {
            if (isMounted && !isVerifyingTOResetTOTP.current) {
                sessionStorage.delete('2fa');
            }
        };
    }, [isMounted]);

    useEffect(() => {
        let interval: NodeJS.Timer;
        if (isDisabled && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setIsDisabled(false);
        }

        return () => clearInterval(interval);
    }, [isDisabled, timer]);

    /**
     * Check login state and email
     */
    useEffect(() => {
        const twoFAEmail = sessionStorage.getData("2fa");
        if (twoFAEmail !== email) {
            toast("Unauthorized page access.", { type: toast.TYPE.ERROR });
            navigate(redirectionRoutes.login.path);
        }
    }, [navigate, email])


    /**
     * Submit Method to call when user clicks on verify button
     * @async
     * @param {email: string, token: string} values
     * @returns {*}
     */
    const handleSubmit = async (values: Record<string, any>, { setSubmitting }: any) => {
        if (lostDevice) {
            handleVerifyToResetTOTP(values, setSubmitting);
            return;
        }
        setLoading(true);
        setSubmitting(true);
        verify2faAPI({ email: values.email, code: values.code, rememberDevice: values.rememberDevice, deviceID: values.deviceID }).then((response: any) => {
            toast(response.message, { type: toast.TYPE.SUCCESS });
            setLoading(false);
            setSubmitting(false);
            sessionStorage.delete('2fa');
            if(auth0Login) {
                setAuth0Login(true);
            }
            storage.setToken(response.data.token.access.token);
            storage.setRefreshToken(response.data.token.refresh.token);
            setUser(response.data?.user);
            if(!response.data?.user?.profileVerification?.status) {
                navigate(redirectionRoutes.profile.path, { replace: true });
            } else {
                navigate(redirectionRoutes.dashboard.path, { replace: true });
            }
        }).catch((error: any) => {
            setLoading(false);
            setSubmitting(false);
            toast(error.data.message, { type: toast.TYPE.ERROR });
            //if user locked due to multiple attempt, navigate to the login
            if (error?.data?.messageCode === 3056) {
                navigate("/login")
            }
        })
    }

    /**
     * Regenerate and resend the code to the requesting user.
     */
    const resendCode = () => {
        setRegenerating(true);
        regenerate2faAPI({ email }).then((response: any) => {
            setRegenerating(false);
            setIsDisabled(true);
            setTimer(coolDownTime);
            toast(response.message, { type: toast.TYPE.SUCCESS });
        }).catch((error: any) => {
            console.error(error)
            setRegenerating(false);
            toast(error.data.message, { type: toast.TYPE.ERROR });
            //if user locked due to multiple attempt, navigate to the login
            if (error?.data?.messageCode === 3056) {
                navigate("/login")
            }
        })
    }

    const handleVerifyToResetTOTP = (values: any, setSubmitting: any) => {
        setLoading(true);
        setSubmitting(true);
        verifyToResetTOTPAPI({ email: values.email, code: values.code }).then((response: any) => {
            isVerifyingTOResetTOTP.current = true;
            setLoading(false);
            setSubmitting(false);
            const totpData = response?.data?.totpData;
            navigate(redirectionRoutes.TOTPSetup.path, { state: { email: values.email, auth0Login: false, totpData: totpData }, replace: true });
        }).catch((error: any) => {
            setLoading(false);
            setSubmitting(false);
            toast(error.data.message, { type: toast.TYPE.ERROR });
            //if user locked due to multiple attempt, navigate to the login
            if (error?.data?.messageCode === 3056) {
                navigate("/login")
            }
        })
    }

    return (
        <React.Fragment>
            <Loader isLoading={loading} />
            <Row className="g-0 vh-100">
                <Col
                    md={6}
                >
                    <Stack className='custom-primary-gradient text-center justify-content-around h-100 p-4' gap={4}>
                        <div className='user-select-none'>
                            <Image
                                fluid={true}
                                src={LOGO_WHITE}
                                alt={`${AppName} Logo`}
                                width={240}
                                height={80}
                            />
                        </div>
                        <div>
                            <Image
                                fluid={true}
                                src={LOGIN_BANNER}
                                alt={`${AppName}`}
                                width={500}
                                height={334}
                            />
                        </div>
                        <div className='lh-sm text-white'>
                            <div className='fw-light fs-4'>AI-Powered</div>
                            <div className='fw-semibold fs-1 text-uppercase'>Penetration Testing</div>
                            <div className='fw-light fs-4'>for Enterprises</div>
                        </div>
                    </Stack>
                </Col>
                <Col md={6} className="col-md-6 d-flex flex-column p-3">
                    <div className="custom-width-320 d-flex flex-column flex-grow-1 mw-100 mx-auto py-3">
                        <div className='my-auto py-4'>
                            <p className="mb-1 opacity-75 text-uppercase">Multi Factor Authentication</p>
                            <h1 className="fw-semibold mb-4 pb-1 fs-2">{AppName}</h1>
                            {/* NEW: Admin Notification Section */}
                            {(isChildUser && !lostDevice) && (
                                <Alert variant="info" className="d-flex align-items-start gap-2 fs-14 border-0 bg-light-blue mt-2 mb-4">
                                    <FaInfoCircle className="mt-1 flex-shrink-0" />
                                    <div>
                                        Your account administrator has enabled Two-Factor Authentication using the Email method to enhance your account security. Please enter the OTP sent to your registered email address to complete your login.
                                    </div>
                                </Alert>
                            )}
                            <CommonFormikComponent
                                validationSchema={ForgotSchema}
                                initialValues={initialValues}
                                onSubmit={handleSubmit}
                            >
                                {(formikProps) => (
                                    <>
                                        <CustomInputText
                                            wrapperClassname="mb-2 pb-1"
                                            controlId="code"
                                            label='Code'
                                            placeholder="6 digit code"
                                            touched={formikProps.touched.code}
                                            handleBlur={formikProps.handleBlur}
                                            handleChange={formikProps.handleChange}
                                            errorsField={formikProps.errors.code}
                                            value={formikProps.values.code}
                                            validationSchema={ForgotSchema}
                                        />
                                         {
                                            !lostDevice ? 
                                            <Row xs="auto" className="align-items-center justify-content-between gx-0 mb-3">
                                                <Col>
                                                    <CustomCheckbox
                                                        controlId="rememberDevice"
                                                        value="Trust device for 14 days"
                                                        label='Trust device for 14 days'
                                                        checked={formikProps?.values.rememberDevice}
                                                        onChange={(e: any) => {
                                                            formikProps.setFieldValue('rememberDevice', e.target.checked);
                                                        }}
                                                    />
                                                </Col>
                                            </Row> : ''
                                        }
                                        <Stack direction="horizontal" gap={3} className="justify-content-end">
                                            <Button className="w-100" variant="primary" type="submit" disabled={formikProps.isSubmitting || loading}>
                                                {loading ? 'Verifying...' : 'Verify'}
                                            </Button>
                                            {
                                                !lostDevice ? 
                                                <Button className="w-100" variant="primary" type="button" onClick={resendCode} disabled={regenerating || isDisabled}>
                                                    {isDisabled ? `Resend in ${timer}s` : "Resend Code"}
                                                </Button> : ''
                                            }
                                        </Stack>
                                    </>
                                )}
                            </CommonFormikComponent>
                        </div>
                    </div>
                </Col>
            </Row>
        </React.Fragment>
    )
}